import {
  ToastNotificationDefaultTimeout,
  segmentEvents,
  validEvents,
  User,
  BreadcrumbItem,
  PageConfig,
  RenderConfs,
  Toast,
  DefaultItem,
  ItemConditionOptions,
  SwatchAppState,
  UiAppState
} from "@/types";
import VueRouter from "vue-router";
import { StateContext } from ".";
import Config from "../../public/config.json";

import { get, post, put } from "../api";
import { timeToDate } from "@/utils";

function returnDeviceType(): string {
  if (window.matchMedia("(max-width: 480px)")) {
    return "mobile";
  } else if (window.matchMedia("(max-width: 768px)")) {
    return "tablet";
  } else {
    return "desktop";
  }
}

function transformEcResponse(ecInfoRes: { [key: string]: any }[]) {
  return ecInfoRes.map(ecInfo => {
    return {
      name: ecInfo.id,
      display_name: ecInfo.display_name,
      city: ecInfo.city
    };
  });
}

function userAccessPermissions(userPermissions: {
  [key: string]: any;
}): { hasReadAccess: boolean; hasWriteAccess: boolean } {
  //filter out the permission of this app
  return {
    hasReadAccess: userPermissions && userPermissions[Config.swatchUserReadPermission] == "allow",
    hasWriteAccess: userPermissions && userPermissions[Config.swatchUserReadPermission] == "allow"
  };
}

function transformProducts(
  searchResults: any
): { data: any; totalcount: number } {
  let productBrand: any;
  //transformt the results from CMS search for add inventory search results
  let transformedResults = searchResults.hits.hits.map((productSrc: any) => {
    if (productSrc._source.features) {
      productBrand = productSrc._source.features.find(
        (feature: { feature: string }) => {
          return feature.feature === "Brand";
        }
      );
    }
    const productCategory = productSrc._source.category
      ? productSrc._source.category.display_name
      : null;
    let productOptions = [] as any;
    //get all fields that have an skuimpact
    let productFields = productSrc._source.fields.filter(
      (field: { is_sku_impact: 0 | 1 }) => {
        return field.is_sku_impact === 1;
      }
    );
    //create all combinations of field options that result in an sku
    let skuFieldOption: any = [];
    for (let field of productFields) {
      let tempFieldOptions: Array<string> = [];
      for (let option of field.options) {
        option.field_options = field.id + "-" + option.id;
        if (skuFieldOption.length > 0) {
          for (let tempOption of skuFieldOption) {
            option.display_name =
              tempOption.display_name + " " + option.display_name;
            option.swatch_image_url = tempOption.swatch_image_url;
            option.field_options =
              tempOption.field_options + "," + option.field_options;
            tempFieldOptions.push(option);
          }
        }
      }
      skuFieldOption =
        tempFieldOptions.length > 0 ? tempFieldOptions : field.options;
    }
    //each field has options and all options of all fields are finishes of the EC Collaterals
    productOptions = productOptions.concat(
      skuFieldOption.map((option: any) => {
        return {
          displayName: option.display_name,
          name: option.code,
          selected: false,
          meta: 0,
          subtext: option.code,
          imageurl: option.swatch_image_url,
          field_options: option.field_options,
          samples: {
            data: [],
            pageConfig: JSON.parse(JSON.stringify(PageConfig))
          }
        };
      })
    );
    let subText = productCategory ? productCategory : "-";
    subText += productCategory ? " / " : "";
    subText += productBrand ? productBrand.value[0] : "-";
    return {
      default_sku: productSrc._source.default_sku,
      sku_code: productSrc._source.code,
      productId: productSrc._source.id,
      sku_name: {
        name: productSrc._source.name,
        brand: productBrand ? productBrand.value[0] : "",
        img_url:
          (productSrc._source &&
            productSrc._source.cover_image &&
            productSrc._source.cover_image.medium) ||
          "",
        subText: subText
      },
      action: null,
      subText: subText,
      finishes: productOptions
    };
  });
  return { data: transformedResults, totalcount: searchResults.hits.total };
}

function transformInventoryProducts(
  searchResults: any
): { data: any; totalcount: number } {
  //transform data for the EC's inventory products view
  let transformedResults = searchResults.response.results.map((result: any) => {
    let productBrand,
      img_url = null,
      subText = null;
    //more info of sku is available under sku_data and fetch those details
    if (result.sku_data) {
      productBrand = result.brand;
      if (result.sku_data.images && result.sku_data.images.length > 0) {
        img_url = result.sku_data.images[0].image_links.list;
      }
      subText = result.sku_data.category
        ? result.sku_data.category.display_name
        : "";
      subText += result.sku_data.category ? " / " : "";
      subText += productBrand ? productBrand : "";
    }
    return {
      sku_name: {
        name: result.name,
        brand: productBrand ? productBrand : "-",
        img_url: img_url
      },
      sku_code: result.product_code,
      availability: {
        available: result.available_count,
        issued: result.issued_count
      },
      subText: subText,
      action: null
    };
  });
  return { data: transformedResults, totalcount: searchResults.response.count };
}

function transformInventorySKUs(
  searchResults: any
): { data: any; totalcount: number } {
  //transform data for the EC's inventory skus view
  let transformedResults = searchResults.response.results.map((result: any) => {
    let productBrand,
      img_url = null,
      subText = null;
    //more info of sku is available under sku_data and fetch those details
    if (result.sku_data) {
      productBrand = result.brand;
      if (
        result.sku_data.fields &&
        Object.keys(result.sku_data.fields).length > 0
      ) {
        img_url =
          result.sku_data.fields[Object.keys(result.sku_data.fields)[0]].option
            .swatch_image_url;
      }
      //if image is not in the fields then pick from images
      if (!img_url) {
        if (result.sku_data.images && result.sku_data.images.length > 0) {
          img_url = result.sku_data.images[0].image_links.list;
        }
      }
      subText = result.sku_data.category
        ? result.sku_data.category.display_name
        : "";
      subText += result.sku_data.category ? " / " : "";
      subText += productBrand ? productBrand : "";
    }
    return {
      sku_name: {
        name: result.sku_display_name,
        brand: productBrand ? productBrand : "-",
        img_url: img_url
      },
      sku_code: result.sku_code,
      availability: {
        available: result.available_count,
        issued: result.issued_count
      },
      subText: subText,
      action: null,
      finish_data: transformFinishData(result)
    };
  });
  return { data: transformedResults, totalcount: searchResults.response.count };
}

function transformFinishData(data: any) {
  //convert the finish data to be shown on add/edit finish page
  let img_url = null;
  const productBrand = data.brand;
  const productCategory = data.sku_data
    ? data.sku_data.category.display_name
    : "-";
  let subText = productCategory ? productCategory : "-";
  subText += productCategory ? " / " : "";
  subText += productBrand ? productBrand : "-";
  if (data.sku_data.fields && Object.keys(data.sku_data.fields).length > 0) {
    img_url =
      data.sku_data.fields[Object.keys(data.sku_data.fields)[0]].option
        .swatch_image_url;
  }
  //if image is not in the fields then pick from images
  if (!img_url) {
    if (data.sku_data.images && data.sku_data.images.length > 0) {
      img_url = data.sku_data.images[0].image_links.list;
    }
  }
  const finishData = {
    sku_code: data.product_code,
    sku_name: {
      name: data.name,
      brand: productBrand ? productBrand : null,
      img_url: img_url,
      subText: subText
    },
    action: null,
    subText: subText,
    finishes: [
      {
        displayName: data.sku_display_name ? data.sku_display_name : data.name,
        name: data.sku_code,
        selected: true,
        meta: 0,
        subtext: data.sku_code,
        sku_code: data.sku_code,
        imageurl: img_url,
        samples: {
          data: [],
          pageConfig: Object.assign({}, PageConfig)
        }
      }
    ]
  };
  return finishData;
}

function transformInventorySamples(
  searchResults: any
): { data: any; totalcount: number } {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric"
  };
  //transform data for the EC's inventory samples view
    let transformedResults = searchResults.response.results.map((result: any) => {
    let issued_to = null;
    //if lending data is available then compute the dates of lendings
    if (result.lending) {
      issued_to = {
        display_name: result.lending.issued_to.display_name,
        phone: result.lending.issued_to.phone,
        issue_date: result.lending.issue_date
          ? new Date(timeToDate(result.lending.issue_date)).toLocaleDateString(
              "en-US",
              options as any
            )
          : "-",
        return_date: result.lending.return_date
          ? new Date(timeToDate(result.lending.return_date)).toLocaleDateString(
              "en-US",
              options as any
            )
          : "-",
        expected_return_date: result.lending.expected_return_date
          ? new Date(
              timeToDate(result.lending.expected_return_date)
            ).toLocaleDateString("en-US", options as any)
          : "-"
      };
    }
    return {
      name: result.name,
      sample_id: result.unique_code,
      inventory_id: result.id,
      condition: result.condition
        ? getCondition(result.condition)
        : DefaultItem,
      location: result.floor ? result.floor : "",
      remarks: result.remarks ? result.remarks : "",
      issue_condition: result.lending
        ? getCondition(result.lending.issue_condition)
        : DefaultItem,
      issue_remarks: result.lending ? result.lending.issue_remarks : "",
      return_condition: result.lending
        ? getCondition(result.lending.return_condition)
        : DefaultItem,
      return_remarks: result.lending ? result.lending.return_remarks : "",
      availability: {
        status: result.status
      },
      issued_to: issued_to,
      action: null,
      created_at: result.created_at? new Date(timeToDate(result.created_at)).toLocaleDateString(
              "en-US",
              options as any
            )
          : "-",
    };
  });
  return { data: transformedResults, totalcount: searchResults.response.count };
}

function getCondition(condition: string) {
  //get the condition as per the the storage type {display_name,name}
  let conditionItem;
  if (condition) {
    conditionItem = ItemConditionOptions.find(item => {
      return item.name === condition;
    });
  } else {
    conditionItem = DefaultItem;
  }
  return conditionItem;
}

function transformInventoryGetSamples(
  searchResults: any
): { data: any; totalcount: number } {
  //convert samples for add/edit finish page
  let transformedResults = searchResults.response.results.map((result: any) => {
    return {
      id: result.id,
      sample_id: result.unique_code,
      condition: result.condition
        ? getCondition(result.condition)
        : DefaultItem,
      location: result.floor,
      action: null,
      isEditing: false,
      is_deleted: false,
      checked: false,
      status: result.status,
      rowId: result.unique_code + Math.ceil(Math.random() * 100000)
    };
  });
  return { data: transformedResults, totalcount: searchResults.response.count };
}

function transformLendingHistory(
  searchResults: any
): { data: any; totalcount: number } {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric"
  };
  //transformt the lendings history data for transaction history view
  let transformedResults = searchResults.response.results.map((result: any) => {
    return {
      sample: {
        sample_id: result.inventory_data.unique_code,
        name: result.inventory_data.name
      },
      issued_to: {
        display_name: result.issued_to.display_name,
        phone: result.issued_to.phone
      },
      issue_date: result.issue_date
        ? new Date(timeToDate(result.issue_date)).toLocaleDateString(
            "en-US",
            options as any
          )
        : "-",
      return_date: result.return_date
        ? new Date(timeToDate(result.return_date)).toLocaleDateString(
            "en-US",
            options as any
          )
        : result.expected_return_date
        ? "Till " +
          new Date(timeToDate(result.expected_return_date)).toLocaleDateString(
            "en-US",
            options as any
          )
        : "-",
      issue_condition: result.issue_condition
        ? getCondition(result.issue_condition)
        : DefaultItem,
      issue_remarks: result.issue_remarks,
      return_condition: result.return_condition
        ? getCondition(result.return_condition)
        : DefaultItem,
      return_remarks: result.return_remarks
    };
  });
  return { data: transformedResults, totalcount: searchResults.response.count };
}

function getEventData(
  eventName: segmentEvents,
  stateContext: StateContext<SwatchAppState>,
  eventProperties?: { [key: string]: any }
): { [key: string]: any } {
  switch (eventName) {
    case "LOGIN_CLICKED":
    case "LOGIN_FAILED":
    case "LOGIN_SUCCESS":
      return {
        device_type: returnDeviceType(),
        device_os: window.navigator.platform,
        device_browser: window.navigator.vendor
      };
    case "CMS_SEARCH":
    case "GET_CMS_PRODUCT_SKU":
    case "EC_CHANGED":
    case "ADD_SAMPLES":
    case "SEARCH_INVENTORY_PRODUCTS":
    case "SEARCH_INVENTORY_SKUS":
    case "SEARCH_INVENTORY_SAMPLES":
    case "SAMPLE_ISSUE":
    case "SKU_OOS_REQUEST":
    case "SAMPLE_RETURN":
    case "SEARCH_LENDINGS":
    case "DOWNLOAD_SINGLE_LABEL":
    case "VIEW_ALL_ITEM_DETAILS":
    case "LOGOUT":
      return Object.assign(
        { user_id: stateContext.state.loggedInUser.id },
        eventProperties ? eventProperties : {}
      );
    default:
      return {};
  }
}

export const appActions = {
  getSelectedSearchProduct(context: StateContext<SwatchAppState>) {
    return context.state.searchedProductSelected;
  },
  getSelectedEc(context: StateContext<SwatchAppState>) {
    return context.state.selectedEc;
  },
  async fetchBouncerUserDetails(
    context: StateContext<SwatchAppState>,
    payload: { user_id: string; query: string }
  ) {
    // fetch bouncer user
    const userResponse = await get({
      endpoint: "bouncer.v2.users.:user_id",
      user_id: payload.user_id,
      query: payload.query
    });
    return userResponse;
  },
  async userPermissionsCheck(
    context: StateContext<SwatchAppState>,
    payload: string
  ) {
    const body = {
      application_name: Config.clientId,
      permissions: [
        Config.swatchUserReadPermission,
        Config.swatchUserWritePermission
      ]
    };
    const userPermissions = await post({
      endpoint: "bouncer.v3.users.check-access",
      body: body,
    });
    return userAccessPermissions(userPermissions);
  },
  async trackSegementEvents(
    context: StateContext<SwatchAppState>,
    payload: { eventName: segmentEvents; properties?: any }
  ) {
    payload.properties = getEventData(
      payload.eventName,
      context,
      payload.properties
    );
    const eventLabel = validEvents[payload.eventName];
    if (context.state.analyticsInstance) {
      context.state.analyticsInstance.track({
        event: eventLabel,
        payload: payload.properties
      });
    }
  },
  async identifyUserInSegment(
    context: StateContext<SwatchAppState>,
    payload: User
  ) {
    if (context.state.analyticsInstance) {
      const userId = "BO" + payload.id;
      context.state.analyticsInstance.identify(userId, {
        email: payload.email,
        name: payload.name
      });
    }
  },
  updateBreadcrumb(
    context: StateContext<SwatchAppState>,
    payload: { data: BreadcrumbItem; router: VueRouter }
  ) {
    let breadcrumb = context.state.breadcrumb;
    let foundIndex = breadcrumb.items.findIndex((item: any) => {
      return item.name === payload.data.name;
    });
    if (foundIndex > -1) {
      breadcrumb.items = breadcrumb.items.slice(0, foundIndex);
    }
    breadcrumb.items.push(payload.data);
    breadcrumb.routeActive = payload.data.name;
    context.commit("setBreadcrumb", breadcrumb);
    if (foundIndex > -1) {
      payload.router.push({ path: payload.data.routerLink });
    }
  },
  async searchCmsProducts(context: StateContext<SwatchAppState>, payload: any) {
    context.dispatch("trackSegementEvents", {
      eventName: "CMS_SEARCH",
      properties: { query: payload.query }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    const cmsProducts = await get({
      endpoint: "cms.api.es.products",
      query: payload.query
    });
    const transformedProducts = transformProducts(cmsProducts);
    payload.totalcount = transformedProducts.totalcount;
    payload.pagecount = Math.ceil(
      transformedProducts.totalcount / payload.limit
    );
    context.commit("storeCmsSearchProducts", {
      data: transformedProducts.data,
      pageConfig: payload
    });
    context.commit("uiModule/hideLoadingModal", null, { root: true });
  },
  async getCmsProductSKUs(
    context: StateContext<SwatchAppState>,
    payload: { productId: number; query: string }
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "GET_CMS_PRODUCT_SKU",
      properties: { query: payload.query, productId: payload.productId }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    const cmsProductSKUs = await get({
      endpoint: "cmsapi.v2.products.:productId.skus",
      productId: payload.productId,
      query: payload.query
    });
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return cmsProductSKUs[0];
  },
  async searchInventoryProducts(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SEARCH_INVENTORY_PRODUCTS",
      properties: { query: payload.query }
    });
    context.commit("storeInventorySearchProducts", {
      data: [],
      pageConfig: JSON.parse(JSON.stringify(PageConfig))
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    context.commit("storeInventorySearchProducts", {
      data: [],
      pageConfig: payload
    });
    const inventoryProducts = await get({
      endpoint: "ragnarok.swatch.v1.inventory.product.search",
      query: payload.query
    });
    let transformedProducts;
    if (inventoryProducts.response) {
      transformedProducts = transformInventoryProducts(inventoryProducts);
      payload.totalcount = transformedProducts.totalcount;
      payload.pagecount = Math.ceil(
        transformedProducts.totalcount / payload.limit
      );
      context.commit("storeInventorySearchProducts", {
        data: transformedProducts.data,
        pageConfig: payload
      });
    } else {
      context.dispatch(
        "uiModule/addToast",
        {
          severity: "error",
          message: inventoryProducts.error
        },
        { root: true }
      );
    }
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return transformedProducts;
  },
  async searchInventorySKUs(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SEARCH_INVENTORY_SKUS",
      properties: { query: payload.query }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    context.commit("storeInventorySearchSKUs", {
      data: [],
      pageConfig: JSON.parse(JSON.stringify(PageConfig))
    });
    const inventorySKUs = await get({
      endpoint: "ragnarok.swatch.v1.inventory.sku.search",
      query: payload.query
    });
    let transformedSKUs;
    if (inventorySKUs.response) {
      //transform and store the finishData for edit finish
      // if (inventorySKUs.response.results.length > 0) {
      //   context.dispatch(
      //     "transformAndSaveFinishData",
      //     inventorySKUs.response.results[0]
      //   );
      // }
      transformedSKUs = transformInventorySKUs(inventorySKUs);
      payload.totalcount = transformedSKUs.totalcount;
      payload.pagecount = Math.ceil(transformedSKUs.totalcount / payload.limit);
      context.commit("storeInventorySearchSKUs", {
        data: transformedSKUs.data,
        pageConfig: payload
      });
    } else {
      context.dispatch(
        "uiModule/addToast",
        {
          severity: "error",
          message: inventorySKUs.error
        },
        { root: true }
      );
    }
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return transformedSKUs;
  },
  async searchInventorySamples(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    //to show the samples in EC's inventory
    context.dispatch("trackSegementEvents", {
      eventName: "SEARCH_INVENTORY_SAMPLES",
      properties: { query: payload.query }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    context.commit("storeInventorySearchSamples", {
      data: [],
      pageConfig: JSON.parse(JSON.stringify(PageConfig))
    });
    const inventorySamples = await get({
      endpoint: "ragnarok.swatch.v1.inventory.search",
      query: payload.query
    });
    let transformedSamples;
    if (inventorySamples.response) {
      transformedSamples = transformInventorySamples(inventorySamples);
      //transform and store the finishData for edit finish
      if (inventorySamples.response.results.length > 0) {
        context.dispatch(
          "transformAndSaveFinishData",
          inventorySamples.response.results[0]
        );
      }
      payload.totalcount = transformedSamples.totalcount;
      payload.pagecount = Math.ceil(
        transformedSamples.totalcount / payload.limit
      );
      context.commit("storeInventorySearchSamples", {
        data: transformedSamples.data,
        pageConfig: payload
      });
    } else {
      context.dispatch(
        "uiModule/addToast",
        {
          severity: "error",
          message: inventorySamples.error
            ? inventorySamples.error
            : "Some error occured"
        },
        { root: true }
      );
    }
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return transformedSamples;
  },
  transformAndSaveFinishData(context: StateContext<SwatchAppState>, data: any) {
    const finishData = transformFinishData(data);
    context.commit("setSelectedInventoryFinish", finishData);
  },
  async getInventorySamples(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    //to fetch the samples of a finish while add/edit to show samples that are already there
    context.dispatch("trackSegementEvents", {
      eventName: "SEARCH_INVENTORY_SAMPLES",
      properties: { query: payload.query }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    const inventorySamples = await get({
      endpoint: "ragnarok.swatch.v1.inventory.search",
      query: payload.query
    });
    let transformedSamples;
    if (inventorySamples.response) {
      transformedSamples = transformInventoryGetSamples(inventorySamples);
      payload.totalcount = transformedSamples.totalcount;
      payload.pagecount = Math.ceil(
        transformedSamples.totalcount / payload.limit
      );
    } else {
      context.dispatch(
        "uiModule/addToast",
        {
          severity: "error",
          message: inventorySamples.error
        },
        { root: true }
      );
    }
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return {
      data: transformedSamples ? transformedSamples.data : [],
      pageConfig: payload
    };
  },
  async issueInventorySample(
    context: StateContext<SwatchAppState>,
    payload: {
      inventory_id: number;
      issued_to: number;
      issue_remarks: string;
      expected_return_date: string;
    }
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SAMPLE_ISSUE",
      properties: { query: payload }
    });
    const inventoryIssue = await post({
      endpoint: "ragnarok.swatch.v1.lendings.create",
      body: payload
    });
    return inventoryIssue;
  },
  async returnInventorySample(
    context: StateContext<SwatchAppState>,
    payload: {
      inventory_id: number;
      data: object;
    }
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SAMPLE_RETURN",
      properties: { query: payload }
    });
    const inventoryReturn = await put({
      endpoint: "ragnarok.swatch.v1.lendings.:inventoryId.return",
      body: payload
    });
    return inventoryReturn;
  },
  async requestInventoryOOSItem(
    context: StateContext<SwatchAppState>,
    payload: {
      sku_code: string;
      created_by: string | number;
      ec_id: string | number;
    }
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SKU_OOS_REQUEST",
      properties: { query: payload }
    });
    const inventoryOOSRequest = await post({
      endpoint: "ragnarok.swatch.v1.oos-requests",
      body: payload
    });
    return inventoryOOSRequest;
  },
  async searchLendingHistory(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "SEARCH_LENDINGS",
      properties: { query: payload.query }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    context.commit("storeLendingHistory", {
      data: [],
      pageConfig: JSON.parse(JSON.stringify(PageConfig))
    });
    const lendingsHistory = await get({
      endpoint: "ragnarok.swatch.v1.lendings.search",
      query: payload.query
    });
    const transformedHistory = transformLendingHistory(lendingsHistory);
    payload.totalcount = transformedHistory.totalcount;
    payload.pagecount = Math.ceil(
      transformedHistory.totalcount / payload.limit
    );
    context.commit("storeLendingHistory", {
      data: transformedHistory.data,
      pageConfig: payload
    });
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    return transformedHistory;
  },
  async saveSwatchInventory(
    context: StateContext<SwatchAppState>,
    payload: any
  ) {
    context.dispatch("trackSegementEvents", {
      eventName: "ADD_SAMPLES",
      properties: { query: payload }
    });
    context.commit("uiModule/showLoadingModal", null, { root: true });
    const saveInventoryResponse: { [key: string]: any } = await post({
      endpoint: "ragnarok.swatch.v1.inventory.create",
      body: payload
    });
    context.commit("uiModule/hideLoadingModal", null, { root: true });
    if (saveInventoryResponse.response) {
      return saveInventoryResponse;
    } else {
      context.dispatch(
        "uiModule/addToast",
        {
          severity: "error",
          message: saveInventoryResponse.error
        },
        { root: true }
      );
      return Promise.reject();
    }
  },
  async downloadTemplatePdf(
    context: StateContext<SwatchAppState>,
    payload: { [key: string]: any }
  ) {
    const requestPayload = {
      data: {
        data: payload,
        key: new Date().getTime().toString(),
        renderconfs: RenderConfs
      },
      templateName: "swatch-inventory"
    };
    const samplesPdf = await post({
      endpoint: "template.api.v2.template.:templateName.pdf",
      body: requestPayload
    });
    return samplesPdf;
  },
  async getEcInfoOfUser(context: StateContext<SwatchAppState>) {
    if (context.state.loggedInUser) {
      const ecInfoRes = await get({
        endpoint: "wakanda.api.v1.users.:userId.ecs",
        user_id: context.state.loggedInUser.id as string
      });
      const ecList = transformEcResponse(ecInfoRes.response);
      //store list of ecs
      context.commit("setEcList", ecList);
      //select the first ecs for now
      context.commit("setSelectedEc", ecList[0]);
      return Promise.resolve();
    }
    return Promise.reject("Cannot fetch Ec Info");
  },
  async getAllEcs(context: StateContext<SwatchAppState>) {
    if (context.state.loggedInUser) {
      const allEcInfoRes = await get({
        endpoint: "wakanda.api.v1.experience-centers"
      });
      const ecList = transformEcResponse(allEcInfoRes.response.results);
      //store list of ecs
      context.commit("setEcList", ecList);
      //select the first ecs for now
      const selectedEc = ecList.find(ec => {
        return ec.city === context.state.loggedInUser.city;
      });
      context.commit("setSelectedEc", selectedEc ? selectedEc : ecList[0]);
      return Promise.resolve();
    }
    return Promise.reject("Cannot fetch Ec Info");
  },
  selectUserEc(
    context: StateContext<SwatchAppState>,
    payload: string | number
  ) {
    const userEc = context.state.ecList.find(
      (ec: { name: number; display_name: string }) => {
        return payload == ec.name;
      }
    );
    context.commit("setSelectedEc", userEc);
  }
};

export const uiActions = {
  toggleMobileDeviceState(
    context: StateContext<UiAppState>,
    mobileDeviceBool: boolean
  ) {
    // toggle the isOnMobileDevice state
    context.state.isOnMobileDevice = mobileDeviceBool;
  },
  addToast(context: StateContext<UiAppState>, payload: Toast) {
    if (!Object.keys(payload).includes("duration")) {
      payload.duration = ToastNotificationDefaultTimeout;
    }
    context.commit(
      "addToastItem",
      Object.assign(payload, { key: new Date().getTime() })
    );
    //don't clear the toasts that have 0 as duration
    if (payload.duration > 0) {
      setTimeout(() => {
        context.commit("clearToast", payload);
      }, payload.duration);
    }
  }
};
